import React from 'react';
import { connect } from 'react-redux';
import { AppState } from '@Redux/store';
import { Dispatch } from '@Redux/type';
import { container } from '@Container';
import {
  AnnoncementAction,
  ArmyListsAction,
  ArsenalArmiesAction,
  FoldersAction,
  MyContestsAction,
  MyTeamsAction,
  SyncAction,
  UserPreferenceAction,
} from '@Redux/actions';
import { TYPES } from '@Types';
import { useLocation } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import BoardActionComponent from '@Builder/myBuilder/BuilderActionComponent';
import ArmyListNew from '@Builder/myBuilder/builder/new/army_list/ArmyListNew';
import FolderNew from '@Builder/myBuilder/builder/new/folder/FolderNew';
import ArmyListFormat from '@Builder/myBuilder/builder/format/ArmyListFormat';
import ArsenalArmyNew from '@Builder/myArsenal/army/new/ArsenalArmyNew';
import MyArmyListsComponent from '@Builder/myBuilder/builder/MyArmyListsComponent';
import MyArsenalArmiesComponent from '@Builder/myArsenal/army/MyArsenalArmiesComponent';
import MyDraftsComponent from '@Builder/myBuilder/tool/pairing/MyDraftsComponent';
import MyContestsComponent from '@Builder/myBuilder/tournament/MyContestsComponent';
import MyTeamsComponent from '@Builder/myBuilder/tournament/MyTeamsComponent';
import { MyBoardPresenter } from '@Selectors/builder/myBuilder/MyBoardPresenter';
import AnnoncementComponent from '@Components/builder/annoncement/annoncement.component';
import { defaultMessages } from '@I18n/default';

const ArmyListsPageIndex: React.FC<ArmyListsPageIndexProps> = ({
  load,
  setAction,
  myBoard,
  game,
}) => {
  const query = new URLSearchParams(useLocation().search);
  const action = query.get('action');

  React.useEffect(() => {
    load();
    if (action) {
      setAction(action);
    }
  }, []);

  const body = document.body;
  body.classList.remove('theme-default');
  body.classList.add(`theme-${game}`);

  return (
    <div id="army-lists-index">
      <h1 className="text-center">
        <FormattedMessage {...defaultMessages.armyListIndexTitle} />
      </h1>
      <AnnoncementComponent />

      <div>
        <BoardActionComponent />
        {myBoard.createArmyList && <ArmyListNew />}
        {myBoard.createForlder && <FolderNew />}
        {myBoard.formatArmyList && <ArmyListFormat />}
        {myBoard.createArsenalArmy && <ArsenalArmyNew />}
        <MyArmyListsComponent parentId={null} />
        <MyArsenalArmiesComponent />
        <MyDraftsComponent />
        <MyContestsComponent />
        <MyTeamsComponent />
      </div>
    </div>
  );
};

const mapStateToProps = (state: AppState) => ({
  myBoard: MyBoardPresenter(state),
  game: state.userPreference.entity?.game ?? 'default',
});

const mapDispatchToProps = (dispatch: Dispatch) => {
  const arsenalArmiesAction = container.get<ArsenalArmiesAction>(
    TYPES.Actions.ArsenalArmiesAction,
  );
  const myContestsAction = container.get<MyContestsAction>(
    TYPES.Actions.MyContestsAction,
  );
  const myTeamsAction = container.get<MyTeamsAction>(
    TYPES.Actions.MyTeamsAction,
  );
  const foldersAction = container.get<FoldersAction>(
    TYPES.Actions.FoldersAction,
  );
  const armyListsAction = container.get<ArmyListsAction>(
    TYPES.Actions.ArmyListsAction,
  );
  const annoncementAction = container.get<AnnoncementAction>(
    TYPES.Actions.User.AnnoncementAction,
  );
  const userPreferenceAction = container.get<UserPreferenceAction>(
    TYPES.Actions.User.UserPreferenceAction,
  );
  const syncAction = container.get<SyncAction>(TYPES.Actions.SyncAction);

  return {
    load: () => {
      dispatch(armyListsAction.fetchData());
      dispatch(foldersAction.fetchData());
      dispatch(arsenalArmiesAction.fetchData());
      dispatch(myContestsAction.fetchData());
      dispatch(myTeamsAction.fetchData());
      dispatch(annoncementAction.fetchData());
      dispatch(userPreferenceAction.fetchData());
      dispatch(syncAction.sendSyncEvent());
    },
    setAction: (action: string) => dispatch(armyListsAction.setAction(action)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ArmyListsPageIndex);

type ArmyListsPageIndexProps = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps>;
